/**
 * useRestoreFocus — WCAG 2.4.3 (Focus Order) focus-restore for the app's
 * dialogs, which all render through <Modal>, its one caller. On mount it
 * captures whatever currently holds focus; on unmount it returns focus there.
 *
 * WHY A SHARED HOOK: both <AgeGateModal> and <PlatformConsentScreen> are
 * interceptor-driven — a 426/403 pops them, so there is NO trigger element the
 * user clicked and `document.activeElement` is almost always `<body>`. Returning
 * focus to `<body>` strands keyboard/SR users with no focus ring after Escape /
 * "Not now" / "Keep exploring". So when the captured opener is missing, detached,
 * or `<body>`, we fall back to the page's `#main-content` skip-nav target — the
 * user lands somewhere deterministic and can Tab onward. Keeping this in one
 * place means the fallback can't silently drift apart between the two dialogs.
 *
 * The capture happens SYNCHRONOUSLY in the mount effect, which runs before the
 * dialogs' own "focus the field" effect fires — so we record the real opener,
 * not the field we're about to move focus into.
 *
 * `focusLanded` and `focusLandmark` are also named exports. ExerciseLayout's
 * tutor sidebar is not a dialog (its opener unmounts while it is open, so
 * there is no captured element to go back to), but when it closes it needs
 * the same two pieces: the test for focus being on something live, and the
 * last-resort landing on `#main-content`. What it does with them is its own
 * (it has its own landings, and does not count focus resting on a container
 * of them as live): see ExerciseLayout.
 *
 * WHAT CLOSING DOES TO FOCUS (PR #578 review, 2026-09-23; step 3 added
 * 2026-09-26):
 *   1. The opener gets a plain `.focus()` and is NEVER given a tabindex. It was
 *      `document.activeElement` when the dialog opened, so it was focusable by
 *      definition. This hook used to stamp `tabindex="-1"` onto every opener
 *      lacking one — the hack meant only for step 4 — which silently dropped
 *      each opener button out of the Tab order after ONE open/close (even a
 *      Cancel): Tab from "Refresh" skipped "Publish" and landed on <body>. An
 *      opener that can no longer take focus (disabled while its own request
 *      runs, hidden, CSS-hidden, removed) is skipped rather than "focused" into
 *      a no-op.
 *   2. If focus is then on something live, the hook is done: the opener took
 *      it, or — only when step 1 could NOT focus the opener (skipped as
 *      disabled/hidden, or refused by the platform) — the consumer placed it
 *      on purpose in the same handler. A still-enabled opener always wins the
 *      restore: a consumer that wants focus elsewhere after a normal close
 *      must move it AFTER this cleanup has run, not before.
 *   3. Otherwise the caller's `fallbackRef` (Modal / ConfirmDialog
 *      `returnFocusRef`) gets the same plain `.focus()`: the element that
 *      continues the user's work when the confirm itself disabled or removed
 *      the opener — a section heading or status line the consumer renders with
 *      tabIndex={-1} (ARIA APG dialog pattern: "an element that provides
 *      logical work flow"). Measured in Chromium 152 (2026-09-26) on a page
 *      shaped like Admin · Prep → Maintenance: after a landing on <main>, the
 *      NEXT Tab went to the first control on the page and scrolled it to the
 *      top; after a landing on the section heading, Tab went on to that
 *      section's own controls. Never stamped — a fallback that cannot take
 *      focus (no tabindex, disabled, hidden, detached, unset) falls through to
 *      step 4 — and read at CLOSE time, so it may be an element mounted by the
 *      very commit that closes the dialog (Chat's Welcome heading).
 *   4. If focus still landed nowhere (<body>): `#main-content`, the one element
 *      this hook stamps with `tabindex="-1"` (a <main> is not natively
 *      focusable), so the user is never stranded with no ring.
 *   STACKED DIALOGS: when a <Modal> is still open as this dialog closes (the
 *   age gate over the Progress walkthrough whose tutor request raised it),
 *   focus must end INSIDE that dialog, never on the page behind it — a
 *   fallbackRef or #main-content on that page is still behind it — so steps 2-4
 *   give way to one rule. The opener was usually in there and step 1 put focus
 *   back on it. When it could not, that dialog takes focus back itself (its
 *   focusResume, Modal.js): onto the control the user was last on in it, else
 *   where it puts focus on open. Step 1 fails here more often than it looks:
 *   a control that disables itself while its request is out loses focus to
 *   <body> in Chromium, and when that request is what raises the second
 *   dialog, <body> is the opener this hook captures. Settings' state-standards
 *   picker under the parent-invite is one, and closing the invite used to
 *   land on Settings' first switch. (A control still disabled at that moment
 *   cannot take focus back, so the landing is the initial target: an age gate
 *   holds its request until it closes.)
 *   Step 1's attempt comes BEFORE every later check on purpose: during
 *   StrictMode's simulated unmount the dialog is still in the DOM with focus
 *   inside it, so checking first would skip the restore, the re-mount would
 *   capture the dialog's own button as the opener, and a later Cancel would
 *   land on the fallback instead of the opener.
 *   (jsdom hid the disabled-opener half of this: unlike browsers it honours a
 *   tabindex attribute BEFORE the disabled check, so the stamped tabindex="-1"
 *   let a disabled opener take focus in tests only.)
 */
import { useEffect, useRef } from 'react';
import { topModal } from '../components/ui/modalStack';

/**
 * True when `.focus()` would be a silent no-op in a real browser: a disabled
 * control (`:disabled` also covers a disabled <fieldset>'s descendants),
 * anything under [hidden] / [inert], and — where the platform can tell us
 * (Chrome 105+, Firefox 106+, Safari 17.4+) — an element CSS-hidden via
 * display / visibility. jsdom has no layout and no checkVisibility, so the
 * :disabled / [hidden] branches are the ones tests exercise; the "landed?"
 * check in the effect is the browser-side safety net for whatever these miss.
 */
function cannotTakeFocus(el) {
  if (el.matches(':disabled')) return true;
  if (el.closest('[hidden], [inert]')) return true;
  if (
    typeof el.checkVisibility === 'function' &&
    // `visibility: hidden` refuses focus too but the default checkVisibility()
    // reports it visible — ask for it. `checkVisibilityCSS` is the pre-standard
    // Chrome spelling of `visibilityProperty`; passing both covers 105–120.
    !el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true })
  ) {
    return true;
  }
  return false;
}

/**
 * True if `el` is a live element that can take focus right now: not body, not
 * detached, not disabled / hidden. The same bar for the opener (step 1), the
 * fallback (step 3) and the control a dialog resumes on (Modal.js,
 * focusResumeTarget).
 */
export function canTakeFocusNow(el) {
  return Boolean(
    el &&
      el !== document.body &&
      el !== document.documentElement &&
      el.isConnected &&
      typeof el.focus === 'function' &&
      !cannotTakeFocus(el),
  );
}

/** Plain focus — never touches the element's attributes (header, steps 1 + 3). */
function focusPlain(el) {
  try {
    el.focus();
  } catch {
    // Odd node — best-effort; the "landed?" check catches the miss.
  }
}

/** True if focus is ON something live — not <body>, <html>, or nowhere. */
export function focusLanded() {
  const active = document.activeElement;
  return Boolean(
    active &&
      active !== document.body &&
      active !== document.documentElement &&
      active.isConnected,
  );
}

/**
 * Focus the page's `#main-content` landmark (header, step 4). A <main> isn't
 * natively focusable; tabindex="-1" lets it take programmatic focus without
 * entering the Tab order (standard skip-link a11y). The ONLY element this hook
 * ever stamps. `options` are focus()'s own (ExerciseLayout passes
 * preventScroll); the hook itself passes none.
 */
export function focusLandmark(options) {
  const main = document.getElementById('main-content');
  if (!main || typeof main.focus !== 'function') return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  try {
    main.focus(options);
  } catch {
    // Best-effort; the page is still usable.
  }
}

/**
 * Capture the active element on mount; on unmount restore focus to it, else
 * into a dialog that is still open if there is one (beneath this one, or over
 * it), else to `fallbackRef.current`, else to the `#main-content` skip-nav
 * target (header).
 *
 * @param {{ fallbackRef?: { current: HTMLElement | null } }} [options]
 *   fallbackRef — where focus goes when the opener cannot take it back (step 3).
 */
export default function useRestoreFocus({ fallbackRef } = {}) {
  // The ref passed on the LATEST render. The restore cleanup below is created
  // once, on mount, so reading `fallbackRef` there would pin the mount-time
  // ref; a consumer may switch landings between renders (Modal's escapeCfg
  // precedent).
  const latestFallbackRef = useRef(fallbackRef);
  useEffect(() => {
    latestFallbackRef.current = fallbackRef;
  });

  useEffect(() => {
    // Capture synchronously on mount — this effect runs before the dialog's own
    // "focus the first field" effect, so we record the true opener.
    const opener = document.activeElement;
    return () => {
      if (canTakeFocusNow(opener)) focusPlain(opener);
      // Stacked dialogs (header). A closing <Modal> has already left the stack
      // (Modal.js registers before calling this hook), so this one is still open.
      const openDialog = topModal();
      if (openDialog) {
        if (!openDialog.contains(document.activeElement)) openDialog.focusResume();
        return;
      }
      if (focusLanded()) return;
      const fallback = latestFallbackRef.current?.current;
      if (canTakeFocusNow(fallback)) focusPlain(fallback);
      if (focusLanded()) return;
      focusLandmark();
    };
  }, []);
}
